import { Link, NavLink } from 'react-router-dom';
import { List, FolderOpen, Settings } from 'lucide-react';
import { useOrg } from '@/features/org/use-org';
import { cn } from '@/shared/lib/cn';
import { CurrencyToggle } from '@/features/currency/CurrencyToggle';
import { FxTicker } from '@/features/currency/FxTicker';
import { NotificationBell } from './NotificationBell';
import { UserMenu } from './UserMenu';

const NAV_ITEMS = [
  { to: '/listings', label: 'Объявления', Icon: List },
  { to: '/collections', label: 'Подборки', Icon: FolderOpen },
  { to: '/org/settings', label: 'Настройки', Icon: Settings },
] as const;

function navTabClass({ isActive }: { isActive: boolean }) {
  return cn(
    'px-4 flex items-center gap-1.5 text-sm font-medium border-b-2 transition-colors duration-150 shrink-0 whitespace-nowrap',
    isActive
      ? 'text-primary border-primary'
      : 'text-muted-foreground border-transparent hover:text-foreground',
  );
}

function SubscriptionChip({ days }: { days: number }) {
  if (days <= 0) {
    return (
      <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-destructive/15 text-destructive shrink-0">
        Подписка истекла
      </span>
    );
  }
  if (days <= 3) {
    return (
      <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-destructive/15 text-destructive shrink-0">
        Подписка истекает через {days} дн.
      </span>
    );
  }
  if (days <= 7) {
    return (
      <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-[rgba(255,193,116,0.12)] text-[#ffc174] shrink-0">
        Подписка истекает через {days} дн.
      </span>
    );
  }
  return null;
}

export function Header() {
  const { data } = useOrg();

  return (
    <header
      role="banner"
      className="sticky top-0 z-40 h-14 border-b border-[rgba(62,72,79,0.25)] bg-[rgba(11,19,38,0.96)] backdrop-blur-[20px] flex items-stretch px-4"
    >
      {/* Wordmark */}
      <Link
        to="/"
        className="shrink-0 flex items-center mr-6 text-foreground animate-logo-in"
        style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.04em' }}
      >
        DriveBase
      </Link>

      {/* Center: Nav tabs — fill header height via items-stretch */}
      <nav aria-label="Основная навигация" className="flex items-stretch overflow-x-auto">
        {NAV_ITEMS.map(({ to, label, Icon }) => (
          <NavLink key={to} to={to} className={navTabClass}>
            <Icon className="h-[15px] w-[15px] shrink-0 max-[1150px]:h-[17px] max-[1150px]:w-[17px]" />
            <span className="max-[1150px]:hidden">{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Right: subscription chip + currency + account (name/org shown inside UserMenu) */}
      <div className="flex-1 flex items-center justify-end gap-3 min-w-0">
        {data?.subscription && (
          <SubscriptionChip days={data.subscription.days_until_expiry} />
        )}
        <FxTicker />
        <CurrencyToggle />
        <NotificationBell />
        <UserMenu />
      </div>
    </header>
  );
}
